import { isRunningLocal, isRunningStorybook } from '_/utilities/env.utils';
import type { EnvConfig, EnvSlug, RunningEnv } from './env-config.types';
import {
  envConfigRequiredKeys,
  testEnvConfig,
  storybookEnvConfig,
} from './env-config.types';

type ModuleState = {
  envConfig: null | EnvConfig;
};

const state: ModuleState = {
  envConfig: null,
};

export function getEnvConfig(): EnvConfig {
  if (import.meta.env.MODE === 'test') {
    return testEnvConfig;
  }

  if (isRunningStorybook) {
    return storybookEnvConfig;
  }

  if (!state.envConfig) {
    throw new Error('Env Config is not loaded.');
  }

  return state.envConfig;
}

export async function loadEnvConfig(): Promise<EnvConfig> {
  try {
    const response = await fetch('/env/config.json', { cache: 'no-store' });
    const config = await response.json();

    const envConfig = validateConfig(config);
    state.envConfig = envConfig;
    return envConfig;
  } catch (e: any) {
    throw new Error(`Loading env config failed because: ${String(e)}`);
  }
}

function validateConfig(config: Partial<EnvConfig>): EnvConfig {
  const keys = Object.keys(config);
  for (const key of envConfigRequiredKeys) {
    if (!keys.includes(key)) {
      throw new Error(`Env config has no property: '${key}'.`);
    }
  }
  if (config.enableProductAnalytics && !config.enableErrorReporting) {
    throw new Error(
      `Env config 'enableErrorReporting' must be true when 'enableProductAnalytics' is true.`
    );
  }
  // Freeze to prevent modifications.
  return Object.freeze(config) as EnvConfig;
}

export function getRunningEnv(): RunningEnv {
  if (isRunningLocal) {
    return 'local';
  }
  if (isRunningStorybook) {
    return 'storybook';
  }
  return getEnvConfig().environmentSlug;
}

function getEnvSlug(): EnvSlug {
  return getEnvConfig().environmentSlug;
}

export function isEnvDev(): boolean {
  return getEnvSlug().startsWith('dev');
}

export function isEnvQe(): boolean {
  return getEnvSlug().startsWith('qe');
}

export function isEnvAnyLive(): boolean {
  return getEnvSlug().startsWith('live');
}

export function getSiteStatusPageUrl(): string {
  return (
    getEnvConfig().siteStatusPageUrl || 'https://status.obsidiansecurity.com/'
  );
}
